import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "tool-params:v1:";

export function toolParamsStorageKey(toolId: string): string {
    return `${STORAGE_PREFIX}${toolId}`;
}

/** JSON-serializable subset for localStorage. */
export function serializeToolParams(
    values: Record<string, unknown>,
): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) {
        if (
            v === null ||
            typeof v === "string" ||
            typeof v === "number" ||
            typeof v === "boolean"
        ) {
            out[k] = v;
        } else if (Array.isArray(v)) {
            if (v.every(isJsonPrimitiveOrPlainObject)) {
                out[k] = v;
            }
        } else if (typeof v === "object" && v !== null) {
            if (isPlainObject(v)) {
                out[k] = serializeToolParams(v as Record<string, unknown>);
            }
        }
    }
    return out;
}

function isJsonPrimitiveOrPlainObject(x: unknown): boolean {
    if (
        x === null ||
        typeof x === "string" ||
        typeof x === "number" ||
        typeof x === "boolean"
    ) {
        return true;
    }
    if (Array.isArray(x)) {
        return x.every(isJsonPrimitiveOrPlainObject);
    }
    if (typeof x === "object" && x !== null && isPlainObject(x)) {
        return Object.values(x as Record<string, unknown>).every(
            isJsonPrimitiveOrPlainObject,
        );
    }
    return false;
}

function isPlainObject(x: object): boolean {
    const proto = Object.getPrototypeOf(x);
    return proto === Object.prototype || proto === null;
}

export function loadToolParams(
    toolId: string,
): Record<string, unknown> | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(toolParamsStorageKey(toolId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null;
        }
        return parsed as Record<string, unknown>;
    } catch {
        return null;
    }
}

export function saveToolParams(
    toolId: string,
    values: Record<string, unknown>,
): void {
    if (typeof window === "undefined") return;
    try {
        const safe = serializeToolParams(values);
        localStorage.setItem(
            toolParamsStorageKey(toolId),
            JSON.stringify(safe),
        );
    } catch {
        /* quota or private mode */
    }
}

export function clearToolParams(toolId: string): void {
    if (typeof window === "undefined") return;
    try {
        localStorage.removeItem(toolParamsStorageKey(toolId));
    } catch {
        /* ignore */
    }
}

export type ToolParamPersistenceOptions = {
    toolId: string;
    /** Static defaults, or a factory (e.g. fresh nested structures with new ids). */
    defaultPreset:
        | Record<string, unknown>
        | (() => Record<string, unknown>);
    getPresetValues: () => Record<string, unknown>;
    applyPreset: (values: Record<string, unknown>) => void;
    /** Debounce for writes (ms). */
    debounceMs?: number;
};

/**
 * Loads saved params once per mount, persists on change via getPresetValues identity updates.
 * Returns resetParameters() → applies defaultPreset and clears stored key.
 */
export function useToolParamPersistence({
    toolId,
    defaultPreset,
    getPresetValues,
    applyPreset,
    debounceMs = 400,
}: ToolParamPersistenceOptions): {
    resetParameters: () => void;
} {
    const [hydrated, setHydrated] = useState(false);
    const applyPresetRef = useRef(applyPreset);
    applyPresetRef.current = applyPreset;

    useEffect(() => {
        const saved = loadToolParams(toolId);
        if (saved) {
            applyPresetRef.current(saved);
        }
        setHydrated(true);
    }, [toolId]);

    useEffect(() => {
        if (!hydrated) return;
        const handle = window.setTimeout(() => {
            saveToolParams(toolId, getPresetValues());
        }, debounceMs);
        return () => window.clearTimeout(handle);
    }, [hydrated, toolId, getPresetValues, debounceMs]);

    const defaultRef = useRef(defaultPreset);
    defaultRef.current = defaultPreset;

    const resetParameters = useCallback(() => {
        clearToolParams(toolId);
        const def =
            typeof defaultRef.current === "function"
                ? (defaultRef.current as () => Record<string, unknown>)()
                : defaultRef.current;
        applyPresetRef.current(def);
    }, [toolId]);

    return { resetParameters };
}
